import * as React from 'react';
import {
  Link as ChakraLink,
  type LinkProps as ChakraLinkProps,
} from '@chakra-ui/react';

/**
 * Project Link.
 *
 * Wrapper over Chakra v3's `Link` with an NB-compatibility layer so
 * migrated consumers don't all need rewrites:
 *
 *   - NB `onPress` -> standard `onClick`.
 *   - NB `isExternal` -> `target="_blank"` + `rel="noopener noreferrer"`
 *     (defaults to external, matching the previous NativeBase wrapper).
 *   - NB `isUnderlined` defaults to true (underline on); pass
 *     `isUnderlined={false}` (or `_text.textDecoration: 'none'`) for none.
 *   - NB `_text={{ color, ... }}` -> styles flatten onto the Link itself,
 *     since Chakra Link is a plain `<a>` (no nested Text).
 *
 * Default styling matches the previous wrapper: aqua.500 text, underlined,
 * no uppercase, secondary.200 on hover.
 */

type NBTextStyle = Record<string, unknown>;

export type LinkProps = Omit<ChakraLinkProps, 'onClick'> & {
  onPress?: () => void;
  onClick?: ChakraLinkProps['onClick'];
  isExternal?: boolean;
  isUnderlined?: boolean;
  _text?: NBTextStyle;
};

export const Link = React.forwardRef<HTMLAnchorElement, LinkProps>(
  (
    {
      onPress,
      onClick,
      isExternal = true,
      isUnderlined = true,
      _text,
      color,
      textDecoration,
      _hover,
      ...rest
    },
    ref
  ) => {
    const flatText = (_text ?? {}) as ChakraLinkProps;
    const resolvedColor =
      color ?? (flatText.color as string | undefined) ?? 'aqua.500';
    const resolvedDecoration =
      isUnderlined === false
        ? 'none'
        : textDecoration ??
          (flatText.textDecoration as string | undefined) ??
          'underline';

    return (
      <ChakraLink
        ref={ref}
        textTransform="none"
        target={isExternal ? '_blank' : undefined}
        rel={isExternal ? 'noopener noreferrer' : undefined}
        onClick={onClick ?? (onPress ? () => onPress() : undefined)}
        {...flatText}
        {...rest}
        _hover={{ color: 'secondary.200', ..._hover }}
        // Applied after spreads so we beat Chakra's `plain` recipe
        // (`colorPalette.fg`, no underline) while still allowing an
        // explicit consumer `color` / `textDecoration` / `_text` to win.
        color={resolvedColor}
        textDecoration={resolvedDecoration}
      />
    );
  }
);
Link.displayName = 'Link';
